// Shared plumbing for the batch screenshot runners (model page and shots page):
// background-tab-safe capture timing, and packaging captures as a zip or a .pptx deck.

// zip: one png per capture; slides: one slide per group holding its captures
export type BatchOutput = "zip" | "slides";

export interface CapturedImage {
  name: string; // file name inside the zip
  dataUrl: string;
  caption?: string; // shown under the image on a slide
}

// the captures that share one slide
export interface CaptureGroup {
  title: string;
  shots: CapturedImage[];
}

export const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// rAF never fires while the tab is hidden, and a batch should keep running in a
// background tab — fall back to (and race against) a timeout so we never hang
export const nextFrame = () =>
  new Promise<void>((resolve) => {
    if (document.hidden) {
      setTimeout(resolve, 50);
      return;
    }
    let settled = false;
    const done = () => {
      if (!settled) {
        settled = true;
        resolve();
      }
    };
    requestAnimationFrame(done);
    setTimeout(done, 250); // tab may get hidden mid-wait
  });

// html-to-image internally waits on requestAnimationFrame after decoding its snapshot
// image, and rAF never fires while the tab is hidden — freezing background-tab runs.
// While a capture is in flight, race every rAF against a timeout so it always ticks.
let rafPatchDepth = 0;
let originalRaf: typeof window.requestAnimationFrame | null = null;
export async function withBackgroundSafeRaf<T>(work: () => Promise<T>): Promise<T> {
  if (rafPatchDepth === 0) {
    const raf = window.requestAnimationFrame;
    originalRaf = raf;
    window.requestAnimationFrame = (callback: FrameRequestCallback): number => {
      let settled = false;
      const invoke = (time: number) => {
        if (!settled) {
          settled = true;
          callback(time);
        }
      };
      raf.call(window, invoke);
      setTimeout(() => invoke(performance.now()), 100);
      return 0;
    };
  }
  rafPatchDepth++;
  try {
    return await work();
  } finally {
    rafPatchDepth--;
    if (rafPatchDepth === 0 && originalRaf) {
      window.requestAnimationFrame = originalRaf;
      originalRaf = null;
    }
  }
}

export function downloadDataUrl(name: string, href: string) {
  const link = document.createElement("a");
  link.href = href;
  link.download = name;
  link.click();
}

// Packages the captures and downloads them as `<baseName>.zip` / `<baseName>.pptx`
export async function downloadCaptures(output: BatchOutput, groups: CaptureGroup[], baseName: string) {
  if (output === "slides") {
    downloadBlob(await buildSlides(groups), `${baseName}.pptx`);
  } else {
    downloadBlob(await buildZip(groups.flatMap((g) => g.shots)), `${baseName}.zip`);
  }
}

async function buildZip(shots: CapturedImage[]): Promise<Blob> {
  // lazy-loaded so jszip stays out of the main bundle
  const { default: JSZip } = await import("jszip");
  const zip = new JSZip();
  for (const shot of shots) {
    zip.file(shot.name, shot.dataUrl.substring(shot.dataUrl.indexOf(",") + 1), { base64: true });
  }
  return zip.generateAsync({ type: "blob" });
}

// One 16:9 slide per group with its captures in a row at a shared height, each
// captioned underneath when it has a caption, as a .pptx (Google Slides imports it directly)
async function buildSlides(groups: CaptureGroup[]): Promise<Blob> {
  // lazy-loaded so pptxgenjs stays out of the main bundle
  const { default: PptxGenJS } = await import("pptxgenjs");
  const pptx = new PptxGenJS();
  pptx.layout = "LAYOUT_WIDE"; // 13.33 x 7.5 in

  const slideW = 13.333;
  const slideH = 7.5;
  const margin = 0.3;
  const gap = 0.3;
  const titleH = 0.4;
  const rowTop = margin + titleH;

  for (const group of groups) {
    const slide = pptx.addSlide();
    slide.addText(group.title, {
      x: margin,
      y: margin,
      w: slideW - 2 * margin,
      h: titleH,
      fontSize: 12,
      color: "666666",
    });

    // no caption row when none of the captures has one, so a lone image gets the space
    const captionH = group.shots.some((s) => s.caption) ? 0.7 : 0;
    const maxRowH = slideH - rowTop - captionH - margin;

    // every capture gets the same height; widths follow each image's aspect ratio
    const sizes = await Promise.all(group.shots.map((s) => imageSize(s.dataUrl)));
    const aspects = sizes.map(({ width, height }) => width / height);
    const availableW = slideW - 2 * margin - gap * (group.shots.length - 1);
    const h = Math.min(maxRowH, availableW / aspects.reduce((a, b) => a + b, 0));
    const rowW = aspects.reduce((a, b) => a + b * h, 0) + gap * (group.shots.length - 1);
    // centre the image + caption block in the space below the title
    const top = rowTop + (slideH - rowTop - margin - (h + captionH)) / 2;

    let x = (slideW - rowW) / 2;
    group.shots.forEach((shot, i) => {
      const w = aspects[i] * h;
      slide.addImage({ data: shot.dataUrl, x, y: top, w, h });
      if (shot.caption) {
        slide.addText(shot.caption, {
          x,
          y: top + h,
          w,
          h: captionH,
          align: "center",
          fontSize: 28,
          bold: true,
        });
      }
      x += w + gap;
    });
  }

  return (await pptx.write({ outputType: "blob" })) as Blob;
}

function downloadBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  // browsers can drop downloads triggered from a hidden tab — fire it when the tab
  // is next visible instead (immediately if it already is). Deliberately not awaited
  // so restoring the viewer and notifying don't wait for the user to come back.
  whenTabVisible().then(() => {
    downloadDataUrl(name, url);
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  });
}

function whenTabVisible(): Promise<void> {
  if (!document.hidden) return Promise.resolve();
  return new Promise((resolve) => {
    const onVisible = () => {
      if (!document.hidden) {
        document.removeEventListener("visibilitychange", onVisible);
        resolve();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
  });
}

function imageSize(dataUrl: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = reject;
    img.src = dataUrl;
  });
}
